/** The broker group fields the edit drawer changes, in the order the save confirmation lists them. */
export const BROKER_GROUP_FIELD_LABELS = {
	name: 'Name',
	ledger: 'Ledger',
	branchId: 'Insight branch id',
	permissionsGroup: 'Permissions group id',
	country: 'Country',
	state: 'State',
	timezone: 'Timezone',
	sharepointDomain: 'SharePoint domain',
	clientSiteSuffix: 'Client site suffix',
	clientFolder: 'Client folder',
};

/** Sent together whenever any of them changes. */
export const BROKER_GROUP_LOCATION_FIELDS = ['country', 'state', 'timezone'];

/**
 * The edit drawer's starting values for a broker group list item. featureFlags are the ids
 * of the flags that directly target it (from listAllFeatureFlags); the form edits them as a
 * draft and Save adds or removes the broker group on each changed flag.
 */
export const getBrokerGroupFormValues = (brokerGroup, flags) => ({
	featureFlags: flags
		.filter((flag) => (flag.brokerGroups ?? []).includes(brokerGroup.id))
		.map((flag) => flag.id),
	name: brokerGroup.name ?? '',
	ledger: brokerGroup.ledger ?? '',
	branchId: brokerGroup.branchId ?? '',
	permissionsGroup: brokerGroup.permissionsGroup ?? '',
	// The stored country first, so a config on another country shows as a mismatch
	country: brokerGroup.country ?? brokerGroup.countryCode ?? '',
	state: brokerGroup.state ?? null,
	timezone: brokerGroup.timezone ?? '',
	sharepointDomain: brokerGroup.sharepointDomain ?? '',
	clientSiteSuffix: brokerGroup.clientSiteSuffix ?? '',
	// Stored URL-encoded; edited (and sent) as shown in SharePoint
	clientFolder: decodeURIComponent(brokerGroup.clientFolder ?? ''),
});

/**
 * What the admin changed, from the form's starting and current values cast by the
 * validation schema (trimmed etc.): `changed` lists the fields, `before` and `after` their
 * values, and `body` is the update request, with country, state and timezone all sent when
 * any of them changed.
 */
export const getBrokerGroupChanges = (validationSchema, initialValues, values) => {
	const before = validationSchema.cast(initialValues, { assert: false });
	const after = validationSchema.cast(values);
	const changed = Object.keys(BROKER_GROUP_FIELD_LABELS).filter(
		(field) => (before[field] ?? null) !== (after[field] ?? null),
	);
	const locationChanged = changed.some((field) => BROKER_GROUP_LOCATION_FIELDS.includes(field));
	const body = Object.fromEntries([
		...changed
			.filter((field) => !BROKER_GROUP_LOCATION_FIELDS.includes(field))
			.map((field) => [field, after[field]]),
		...(locationChanged
			? BROKER_GROUP_LOCATION_FIELDS.map((field) => [field, after[field] ?? null])
			: []),
	]);
	return { changed, before, after, body };
};

/** The feature flag ids the broker group is being added to and removed from. */
export const getFeatureFlagChanges = (initialValues, values) => ({
	added: values.featureFlags.filter((id) => !initialValues.featureFlags.includes(id)),
	removed: initialValues.featureFlags.filter((id) => !values.featureFlags.includes(id)),
});
